import React, { useState } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Chip,
    IconButton,
    Avatar,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    useTheme,
    alpha,
    TablePagination
} from '@mui/material';
import {
    MoreVert as MoreVertIcon,
    Delete as DeleteIcon,
    School as SchoolIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Pending as PendingIcon,
    Payment as PaymentIcon
} from '@mui/icons-material';
import { EnrollmentWithUser } from '../../../../services/enrollmentService';

interface EnrollmentTableProps {
    enrollments: EnrollmentWithUser[];
    total: number;
    page: number;
    rowsPerPage: number;
    onPageChange: (event: unknown, newPage: number) => void;
    onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onDeleteEnrollment: (enrollmentId: number) => void;
    onUpdatePaymentStatus: (enrollmentId: number, status: 'unpaid' | 'pending' | 'completed' | 'failed' | 'refunded') => void;
}

const EnrollmentTable: React.FC<EnrollmentTableProps> = ({
    enrollments,
    total,
    page,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange,
    onDeleteEnrollment,
    onUpdatePaymentStatus
}) => {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedEnrollment, setSelectedEnrollment] = useState<EnrollmentWithUser | null>(null);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, enrollment: EnrollmentWithUser) => {
        setAnchorEl(event.currentTarget);
        setSelectedEnrollment(enrollment);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedEnrollment(null);
    };

    const handleDeleteClick = () => {
        if (selectedEnrollment) {
            onDeleteEnrollment(selectedEnrollment.id);
        }
        handleMenuClose();
    };

    const handlePaymentStatusChange = (status: 'unpaid' | 'pending' | 'completed' | 'failed' | 'refunded') => {
        if (selectedEnrollment) {
            onUpdatePaymentStatus(selectedEnrollment.id, status);
        }
        handleMenuClose();
    };

    const getPaymentStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
        switch (status) {
            case 'completed': return 'success';
            case 'pending': return 'warning';
            case 'failed': return 'error';
            case 'refunded': return 'info';
            default: return 'default';
        }
    };

    const getPaymentStatusIcon = (status: string) => {
        switch (status) {
            case 'completed': return <CheckCircleIcon fontSize="small" />;
            case 'pending': return <PendingIcon fontSize="small" />;
            case 'failed': return <CancelIcon fontSize="small" />;
            default: return <PaymentIcon fontSize="small" />;
        }
    };

    return (
        <Paper elevation={0} sx={{ borderRadius: 4, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
            <TableContainer>
                <Table>
                    <TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Student</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Enrollment Date</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Payment Status</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {enrollments.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, color: 'text.secondary' }}>
                                        <SchoolIcon sx={{ fontSize: 48, opacity: 0.2 }} />
                                        <Typography variant="body1">No enrollments found matching your criteria.</Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : (
                            enrollments.map((enrollment) => (
                                <TableRow key={enrollment.id} hover>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Avatar
                                                sx={{
                                                    bgcolor: theme.palette.primary.main,
                                                    color: theme.palette.primary.contrastText,
                                                    width: 32,
                                                    height: 32,
                                                    fontSize: '0.875rem'
                                                }}
                                            >
                                                {enrollment.user.firstName?.[0]}{enrollment.user.lastName?.[0]}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="subtitle2" fontWeight="bold">
                                                    {enrollment.user.firstName} {enrollment.user.lastName}
                                                </Typography>
                                                {/* <Typography variant="caption" color="text.secondary">
                                                    Grade {enrollment.user.grade || 'N/A'}
                                                </Typography> */}
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell>{enrollment.user.email}</TableCell>
                                    <TableCell>
                                        {new Date(enrollment.enrollmentDate).toLocaleDateString()}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            icon={getPaymentStatusIcon(enrollment.paymentStatus)}
                                            label={enrollment.paymentStatus.charAt(0).toUpperCase() + enrollment.paymentStatus.slice(1)}
                                            color={getPaymentStatusColor(enrollment.paymentStatus)}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" onClick={(e) => handleMenuOpen(e, enrollment)}>
                                            <MoreVertIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={total}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
            />

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{ sx: { borderRadius: 2, minWidth: 180 } }}
            >
                <MenuItem disabled>
                    <Typography variant="caption" color="text.secondary">
                        Payment Status
                    </Typography>
                </MenuItem>
                <MenuItem onClick={() => handlePaymentStatusChange('completed')}>
                    <ListItemIcon><CheckCircleIcon fontSize="small" color="success" /></ListItemIcon>
                    <ListItemText>Mark as Paid</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handlePaymentStatusChange('pending')}>
                    <ListItemIcon><PendingIcon fontSize="small" color="warning" /></ListItemIcon>
                    <ListItemText>Mark as Pending</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handlePaymentStatusChange('unpaid')}>
                    <ListItemIcon><PaymentIcon fontSize="small" color="action" /></ListItemIcon>
                    <ListItemText>Mark as Unpaid</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main', mt: 1 }}>
                    <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                    <ListItemText>Remove Enrollment</ListItemText>
                </MenuItem>
            </Menu>
        </Paper>
    );
};

export default EnrollmentTable;
